import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AuthContext } from "../common/auth/AuthContext";
import {
  getProviderInfo,
  getSeasonGames,
  getUpcomingGames,
  scheduleRefreshMs,
} from "../services/sportsData";
import useBrowseMode from "./useBrowseMode";
import { GAME_STATUS } from "../utils/gameEngine";

/**
 * The upcoming schedule, from whichever provider is active.
 *
 * Refreshes on an interval rather than only on mount: a game that kicks
 * off while the page is open should move into "Live now" by itself.
 *
 * HOW OFTEN, AND WHY IT IS NOT OUR DECISION
 * -----------------------------------------
 * The interval comes from the service, because the service owns the cache
 * window. A poll inside that window is answered from cache: it cannot
 * bring back a new score, and it used to make this hook stamp
 * `lastUpdatedAt` anyway, so a five-minute-old score sat under a pulsing
 * "Live" pip claiming to be current. Now the schedule decides its own
 * rate (fast while a game is running or a kickoff is due, slow when the
 * whole list is days away) and `lastUpdatedAt` is the moment the data
 * actually left the network, which the service reports.
 *
 * TWO ANSWERS TO ONE QUESTION
 * ---------------------------
 * The service serves the stored copy of the schedule first and the live
 * one a moment later, so a load can deliver twice: once through the
 * promise and once through `onRevalidated`. Both go through the same
 * `deliver`, which is what keeps the list painted throughout. Nothing
 * here ever puts an empty list on screen between the two, because a
 * flash of "no games" followed by a full schedule reads as a bug.
 *
 * The two deliveries can be scheduled in either order, so `deliver`
 * trusts the `revision` the service stamps on each answer rather than
 * which one arrived first, and never lets the stored copy overwrite the
 * live one.
 *
 * Also splits the list into the three groups the Games screen shows, so
 * that grouping is defined once rather than in the page.
 *
 * ONE OPTIONAL SEASON, AND WHAT IT DOES NOT CHANGE
 * ------------------------------------------------
 * Without `season` this hook is the hook it has always been: the rolling
 * window around now, polled at the rate the schedule policy asks for. That
 * is the path every existing caller takes and not a line of it moved.
 *
 * With `season` it loads one named week of one named season instead, and
 * when that season is an archive it does not poll at all. The poll exists
 * to catch a kickoff. Nothing is going to kick off in 2015, so a timer
 * asking every five minutes whether it has would be a request per reader
 * per five minutes in exchange for an answer that cannot change.
 *
 * @param {object} [options]
 * @param {number} [options.limit]
 * @param {number} [options.refreshMs] overrides the service's rate; tests
 *   use it. An archived season ignores it, because an archive never polls.
 * @param {{year: number, seasonType: number, week: number,
 *   isArchive?: boolean}|null} [options.season] null for the live rolling
 *   schedule, which is the default and the unchanged path.
 * @param {boolean} [options.backendCache] overrides storage-backed schedule
 *   cache use. Omit it to let signed-out browse mode skip that storage tier.
 */
export default function useSchedule({
  limit = 10,
  refreshMs,
  season = null,
  backendCache = null,
  enabled = true,
} = {}) {
  const auth = useContext(AuthContext);
  const { browsing } = useBrowseMode();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [stale, setStale] = useState(false);
  const [unmapped, setUnmapped] = useState(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** Which load is current, and how much of its answer has landed. */
  const loadId = useRef(0);
  const delivered = useRef(0);

  /**
   * Read apart into primitives so the dependency list compares by value.
   * A caller that rebuilds its season object every render would otherwise
   * restart the load on every render, which is a refetch loop.
   */
  const year = season?.year ?? null;
  const seasonType = season?.seasonType ?? null;
  const week = season?.week ?? null;
  const isArchive = Boolean(season?.isArchive);
  const effectiveBackendCache =
    backendCache ?? !(browsing && auth?.status !== "signed-in");

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const id = (loadId.current += 1);
      delivered.current = 0;

      if (!enabled) {
        setGames([]);
        setLoading(false);
        setError(null);
        setStale(false);
        setUnmapped(0);
        setLastUpdatedAt(null);
        return;
      }

      if (!quiet) setLoading(true);

      const deliver = (next) => {
        if (!mounted.current || id !== loadId.current) return;

        const revision = Number(next?.revision) || 0;
        if (revision < delivered.current) return;
        delivered.current = revision;

        setGames(next ?? []);
        // The service reports a failed revalidation on the answer itself,
        // because the list it hands back with it is still worth showing.
        setError(next?.scheduleUnavailable ?? null);
        setStale(Boolean(next?.stale));
        // How many of that week's games the provider listed and we could
        // not map to a club. Old seasons only, and the page says so rather
        // than showing a short week with no explanation.
        setUnmapped(Number(next?.unmapped) || 0);
        // The service says when this data was fetched, which is older than
        // now whenever it came from cache. Null rather than a guess: the
        // UI would sooner say nothing than say "just now" untruthfully.
        setLastUpdatedAt(next?.fetchedAt ?? null);
        setLoading(false);
      };

      try {
        const cacheOptions = effectiveBackendCache
          ? {}
          : { backendCache: false };

        deliver(
          await (year == null
            ? getUpcomingGames({
                limit,
                onRevalidated: deliver,
                ...cacheOptions,
              })
            : getSeasonGames({
                year,
                seasonType,
                week,
                limit,
                onRevalidated: deliver,
                ...cacheOptions,
              }))
        );
      } catch (caught) {
        if (!mounted.current || id !== loadId.current) return;
        // A failed refresh keeps whatever we already had on screen: a
        // blank schedule is worse than a slightly stale one.
        if (!quiet) setGames([]);
        setError(caught);
        setLoading(false);
      }
    },
    [effectiveBackendCache, enabled, limit, year, seasonType, week]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Recomputed from whatever is currently on the list, so the moment a
  // game goes live the schedule starts being re-read in seconds instead
  // of minutes, and drops back down again when it finishes.
  //
  // Zero for an archive, which the effect below reads as "no timer". This
  // beats `refreshMs` on purpose: a caller asking for a rate and a caller
  // asking for a season that finished years ago are not in disagreement,
  // the second one just makes the first one meaningless.
  const pollMs = !enabled
    ? 0
    : isArchive
      ? 0
      : (refreshMs ?? scheduleRefreshMs(games));

  useEffect(() => {
    if (!pollMs) return undefined;
    const timer = setInterval(() => load({ quiet: true }), pollMs);
    return () => clearInterval(timer);
  }, [pollMs, load]);

  const groups = useMemo(() => {
    const live = [];
    const upcoming = [];
    const finished = [];

    games.forEach((game) => {
      const status = game.status ?? GAME_STATUS.SCHEDULED;
      if (status === GAME_STATUS.IN_PROGRESS) live.push(game);
      else if (status === GAME_STATUS.FINAL) finished.push(game);
      else upcoming.push(game);
    });

    // Most recent finals first; everything else in kickoff order.
    finished.sort((a, b) => (b.kickoffAt ?? 0) - (a.kickoffAt ?? 0));

    return { live, upcoming, finished };
  }, [games]);

  return {
    games,
    ...groups,
    loading,
    error,
    lastUpdatedAt,
    /** True when this list is a stored copy the provider could not confirm. */
    stale,
    /** The rate this list is being re-read at, in ms. Zero for an archive. */
    pollMs,
    /** True when this list is a finished week rather than live football. */
    isArchive,
    /** Games in this week that could not be mapped to a club we know. */
    unmapped,
    retry: load,
    provider: getProviderInfo(),
  };
}
